import clsx from 'clsx'
import { html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { CommonFormElementSize, PopupPlacement, Size } from '@/common/types'
import { t } from '@/localization/localization'

type Constructor<T = Record<string, unknown>> = {
  new (...args: any[]): T
  prototype: T
}

export interface LabelableElementInterface {
  label?: string
  description?: string
  tooltip?: string
  tooltipPlacement: PopupPlacement
  size: Size
  required: boolean
  disabled: boolean
  optional: boolean
  optionalLabel: string
  subtle: boolean
  tooltipAriaLabel: string
  renderLabel(id?: string, content?: unknown): unknown
}

export const LabelableMixin = <T extends Constructor<LitElement>>(base: T) => {
  class LabelableElement extends base {
    @property() label?: string
    @property() description?: string
    @property() tooltip?: string
    @property() tooltipPlacement: PopupPlacement = 'bottom'
    @property() size: Size = 'sm'
    @property({ type: Boolean, reflect: true }) optional = false
    @property() optionalLabel = t('label.optional')
    @property({ type: Boolean, reflect: true }) subtle = false
    @property({ type: Boolean, reflect: true }) required = false
    @property({ type: Boolean, reflect: true }) disabled = false
    @property({ type: String }) tooltipAriaLabel = t('label.tooltip')

    renderLabel(id?: string, content?: unknown) {
      return html` <div class="label-container">
      ${
        this.label &&
        html`<dap-ds-form-label
          part="label"
          id=${id ?? 'label'}
          exportparts="base: label-base, required: label-required, optional: label-optional"
          .size=${this.size}
          .required=${this.required}
          .disabled=${this.disabled}
          .subtle=${this.subtle}
          .optional=${this.optional}
          .optionalLabel=${this.optionalLabel}
          class=${clsx('label', {
            'label--has-description': !this.description,
          })}
          for="input"
          label=${ifDefined(this.label)}></dap-ds-form-label>`
      }
      </dap-ds-form-label>
      ${
        this.tooltip &&
        html` <dap-ds-tooltip
          part="tooltip"
          content=${this.tooltip}
          mode="toggle"
          placement=${this.tooltipPlacement}>
          <dap-ds-icon-button
            slot="trigger"
            size="lg"
            clean
            aria-label=${this.tooltipAriaLabel}>
            <dap-ds-icon
              name="information-line"
              aria-hidden="true"
              class="info-button"></dap-ds-icon>
          </dap-ds-icon-button>
        </dap-ds-tooltip>`
      }
    </div>
    ${
      this.description &&
      html`
        <dap-ds-typography
          part="description"
          variant="description"
          size=${ifDefined(this.size as CommonFormElementSize)}
          id="description">
          ${this.description}
        </dap-ds-typography>
      `
    }
      ${content}`
    }
  }

  return LabelableElement as Constructor<LabelableElementInterface> & T
}
